"use client";

import { cn } from "@/lib/utils";
import Cookies from "js-cookie";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

const COOKIE_CONSENTS_KEY = "cookie_consents";

type Consents = {
  necessary: boolean;
  functional: boolean;
  statistic: boolean;
  marketing: boolean;
  unclassified: boolean;
};

type Consent = keyof Consents;

const getGTMDataLayer = () => {
  return (
    null == window["dataLayer"] && (window["dataLayer"] = []),
    Array.isArray(window["dataLayer"]) ? window["dataLayer"] : []
  );
};

function GTMCall(...args: any) {
  getGTMDataLayer().push(arguments);
}

const pushGoogleConsent = (consents: Consents, type: string) => {
  GTMCall("consent", type, {
    ad_storage: consents.marketing ? "granted" : "denied",
    ad_user_data: consents.marketing ? "granted" : "denied",
    ad_personalization: consents.marketing ? "granted" : "denied",
    analytics_storage: consents.statistic ? "granted" : "denied",
    functionality_storage: consents.functional ? "granted" : "denied",
    personalization_storage: consents.necessary ? "granted" : "denied",
    security_storage: "granted",
  });

  const approvedConsents = Object.entries(consents)
    .filter(([_, checked]) => checked)
    .map(([key]) => key as Consent);

  approvedConsents.forEach((consent: Consent) => {
    GTMCall("event", `cookie_cat_${consent}`);
  });
};

const setCookieConsents = (consents: Consents) => {
  const approvedConsents = Object.entries(consents)
    .filter(([_, checked]) => checked)
    .map(([key]) => `cookie_cat_${key}`);

  const deniedConsents = Object.entries(consents)
    .filter(([_, checked]) => !checked)
    .map(([key]) => `cookie_cat_${key}`);

  Cookies.set(
    COOKIE_CONSENTS_KEY,
    JSON.stringify({
      consents_approved: approvedConsents,
      consents_denied: deniedConsents,
    }),
    { expires: 365 },
  );
};

const getCookieConsents = (): null | Consents => {
  const cookieConsents = Cookies.get(COOKIE_CONSENTS_KEY);
  if (!cookieConsents) {
    return null;
  }

  const { consents_approved, consents_denied } = JSON.parse(cookieConsents);
  const consents = {
    necessary: true,
    functional: false,
    statistic: false,
    marketing: false,
    unclassified: false,
  };

  consents_approved.forEach((consent: string) => {
    const [, key] = consent.split("cookie_cat_");
    consents[key as Consent] = true;
  });

  consents_denied.forEach((consent: string) => {
    const [, key] = consent.split("cookie_cat_");
    consents[key as Consent] = false;
  });

  return consents;
};

const CookieConsentContext = createContext<{
  modalOpened: boolean;
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  consents: Consents;
  toggleConsent: (consent: Consent) => void;
  allowSelectedConsents: () => void;
  allowAllConsents: () => void;
}>({
  modalOpened: false,
  setModalOpened: () => {},
  consents: {} as Consents,
  toggleConsent: (consent: Consent) => {},
  allowSelectedConsents: () => {},
  allowAllConsents: () => {},
});

const CookieConsentContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [consents, setConsents] = useState({
    necessary: true,
    functional: false,
    statistic: false,
    marketing: false,
    unclassified: false,
  });

  const toggleConsent = (consent: Consent) => {
    setConsents((prev) => ({ ...prev, [consent]: !prev[consent] }));
  };

  const allowSelectedConsents = () => {
    setCookieConsents(consents);
    pushGoogleConsent(consents, "update");
    setModalOpened(false);
  };

  const allowAllConsents = () => {
    const allConsents = {
      necessary: true,
      functional: true,
      statistic: true,
      marketing: true,
      unclassified: true,
    };

    setConsents(allConsents);
    setCookieConsents(allConsents);
    pushGoogleConsent(allConsents, "update");
    setModalOpened(false);
  };

  useEffect(() => {
    try {
      const cookieConsents = getCookieConsents();
      if (!cookieConsents) {
        pushGoogleConsent(
          {
            necessary: true,
            functional: false,
            statistic: false,
            marketing: false,
            unclassified: false,
          },
          "default",
        );
        setTimeout(() => {
          setModalOpened(true);
        }, 1000);
        return;
      }

      setConsents(cookieConsents);
      pushGoogleConsent(cookieConsents, "default");
    } catch (e) {
      setModalOpened(true);
    }
  }, []);

  return (
    <CookieConsentContext.Provider
      value={{
        modalOpened,
        setModalOpened,
        consents,
        toggleConsent,
        allowSelectedConsents,
        allowAllConsents,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

const useCookieConsent = () => useContext(CookieConsentContext);

const CookieConsentSymbol = () => {
  const { modalOpened, setModalOpened } = useCookieConsent();
  if (modalOpened) {
    return <></>;
  }

  return (
    <div
      className="cursor-pointer max-md:-translate-x-1 max-md:translate-y-1"
      onClick={() => setModalOpened(true)}
    >
      <svg
        viewBox="0 0 233.333 216.225"
        className="size-7 drop-shadow-sm transition hover:scale-110 hover:drop-shadow-lg"
      >
        <g>
          <path
            className="fill-[#d09563]"
            d="M83.9 211.026c-.751-.528-1.656-.136-2.476-.37a132.027 132.027 0 0 1-13.685-4.7 142.731 142.731 0 0 1-13.107-6.182 102.786 102.786 0 0 1-15.865-10.346 93.17 93.17 0 0 1-11.674-11.464c-3.01-3.418-5.552-7.144-8.262-10.76-1.343-1.791-2.692-3.6-3.91-5.441a96.82 96.82 0 0 1-3.7-6.039 95.341 95.341 0 0 1-4.936-10.138 85.245 85.245 0 0 1-4.2-13.509 86.277 86.277 0 0 1-2.049-21.9A102.816 102.816 0 0 1 3.02 88.7a130 130 0 0 1 3.871-12.636c1.982-5.615 3.4-11.372 5.181-17.034a42.473 42.473 0 0 1 8.778-15.386 133.246 133.246 0 0 1 10.431-10.167 109.6 109.6 0 0 1 11.083-9.07c3.4-2.374 6.944-4.571 10.579-6.64C59.2 14.2 65.554 10.8 71.914 7.4 75.355 5.562 79.2 4.793 83 3.864c3.753-.919 7.582-1.366 11.39-1.914 3.96-.569 7.961-.952 11.96-1.217A190.391 190.391 0 0 1 127.414.1a72.682 72.682 0 0 1 12.737 1.725c4.6 1.036 9.106 2.3 13.566 3.765q7.941 2.6 16.126 4.488c4.447 1.035 8.888 2.1 13.247 3.4A59.793 59.793 0 0 1 199.4 20.7a35.043 35.043 0 0 1 8.459 8.249 146.852 146.852 0 0 1 8.347 13.218c1.514 2.522 3.2 4.954 4.549 7.56 1.453 2.811 2.864 5.639 4.033 8.567A91.77 91.77 0 0 1 229.4 73c1.176 5.458 2.407 10.91 3.18 16.435a78.871 78.871 0 0 1 .625 15.423 115.953 115.953 0 0 1-1.3 12.191c-.864 5.281-1.655 10.573-2.577 15.845a119.917 119.917 0 0 1-1.9 8.977 77.5 77.5 0 0 1-6.5 17.693 144.8 144.8 0 0 1-9.619 15.985 78.552 78.552 0 0 1-9.728 11.23 75.2 75.2 0 0 1-7.192 5.83 123.57 123.57 0 0 1-10.709 6.991c-5.135 3.029-10.446 5.774-15.832 8.411a76.974 76.974 0 0 1-17.375 5.927 93.092 93.092 0 0 1-16.224 2.188c-6.423.27-12.837-.066-19.248-.288a149.444 149.444 0 0 1-18.981-1.969 87.058 87.058 0 0 1-9.7-1.948 6.649 6.649 0 0 1-2.3-.781l-.119-.113"
          />
          <path
            className="fill-[#be8654]"
            d="M194.374 192.6a123.586 123.586 0 0 1-10.706 6.994c-5.134 3.029-10.445 5.774-15.831 8.41a76.944 76.944 0 0 1-17.375 5.928 93.1 93.1 0 0 1-16.225 2.187c-6.422.271-12.836-.065-19.247-.287a149.64 149.64 0 0 1-18.982-1.969 87.561 87.561 0 0 1-9.7-1.948 6.672 6.672 0 0 1-2.3-.781l-.119-.113c-.752-.528-1.657-.136-2.476-.371a132.117 132.117 0 0 1-13.686-4.7 143.1 143.1 0 0 1-13.106-6.182 102.679 102.679 0 0 1-15.866-10.347 93.093 93.093 0 0 1-11.674-11.463c-3.009-3.418-5.551-7.144-8.262-10.76-1.342-1.791-2.692-3.6-3.909-5.442a96.797 96.797 0 0 1-3.7-6.038s-2.9-5.7 8.754-1.23c9.328 3.574 21.761 7.563 26.46 8.022s20.276-.452 33.136 2.99 20.03 25.9 53.167 22.232a242.579 242.579 0 0 1 59.576.688s5.358-.561 2.071 4.189"
          />
          <g className="fill-[#69300d]">
            <path d="M125.784 78.307c4.524 1.811 9.822.753 12.707-.091a19.9 19.9 0 0 0 .167-3.076 14.969 14.969 0 0 0-.239-3.368c-.55-4.067-3.046-6.141-7.445-5.979a18.354 18.354 0 0 0-11.234 4.14c-2.986 2.448-3.79 5.741-2.17 8.7a9.005 9.005 0 0 1 8.214-.323" />
            <path d="M192.721 57.3a10.779 10.779 0 0 0-.1-4.37 15.788 15.788 0 0 0-7.027-10.1 10.566 10.566 0 0 0-5.8-2.045 17.412 17.412 0 0 0-1.834.162c-3.227.65-5.074 2.81-6.742 5.189-1.521 2.17-2.707 4.525-4.349 6.63a3.583 3.583 0 0 0-.778 2.224 30.759 30.759 0 0 1 7.11 1.217 47.6 47.6 0 0 0 19.521 1.1" />
            <path d="M124.605 30.191a7.486 7.486 0 0 1 6.674 1s1.558 2.572 5.573-4.792a14.174 14.174 0 0 0-.351-4.7 5.753 5.753 0 0 0-4.767-4.72 18.624 18.624 0 0 0-13.8 2.659 6.3 6.3 0 0 0-3.18 4.756 9.605 9.605 0 0 0 .066 2.369 13.653 13.653 0 0 1 3.846 2.8 6.1 6.1 0 0 0 5.936.63" />
            <path d="M84.575 51.493c1.35-4.715-.109-8.854-4.64-11.894A27.835 27.835 0 0 0 70.7 35.7a8.708 8.708 0 0 0-8.857 2.7 14.354 14.354 0 0 0-3.557 7.968 12.375 12.375 0 0 0-.126 2.332c2.282.366 5.2 1.637 8.426 4.978 5.06 5.244 13.221 1.077 17.989-2.181" />
            <path d="M25.948 93.865c3.015 3.851 10.065 1.381 15.152-1.165a23.075 23.075 0 0 0 .375-4.864 21.135 21.135 0 0 0-.606-6.026 4.6 4.6 0 0 0-4.232-3.3 10.616 10.616 0 0 0-2.883.037 13.672 13.672 0 0 0-9.893 6.018 9.656 9.656 0 0 0-1.3 6.693c.74-.051 1.817.6 3.387 2.6" />
            <path d="M197.6 115.831s2.6 2.262 12.94-4.838c.067-.236.15-.461.2-.709a21.343 21.343 0 0 0-.131-6.742c-.494-4.372-3.747-5.88-7.793-5.541a15.516 15.516 0 0 0-7.444 2.613c-2.76 1.858-4.955 4.089-5.614 7.213 1.769 1.242 4.279 3.642 7.841 8" />
            <path d="M100.713 121.043s1.86-2.468 3.924-2.308a19.602 19.602 0 0 0-.064-.766 7.866 7.866 0 0 0-2.715-4.749 19.042 19.042 0 0 0-13.019-5.3c-2.419-.064-4.768.6-6.107 2.561a12.625 12.625 0 0 0-2.45 8.9 20.473 20.473 0 0 1 7.25 3.619c9.822 7.156 13.181-1.957 13.181-1.957" />
            <path d="M40.119 159.144c4.514 7.25 14.911 1.368 14.911 1.368s2.982-1.849 6.078-3.423a31.741 31.741 0 0 0 .286-3.806c.208-3.987-2.337-7.238-6.934-8.739a20.643 20.643 0 0 0-11.315-.353c-5.616 1.319-8.723 5.454-8.09 10.094 1.389.48 3.146 1.779 5.064 4.859" />
            <path d="M151.766 144.779a22.8 22.8 0 0 0 .269-3.332c.151-3.477-2.1-7.589-6.025-9.709a8.962 8.962 0 0 0-8.5-.475c-3.962 1.883-6 5.094-7.055 8.948a9.36 9.36 0 0 0-.338 2.059 23.092 23.092 0 0 1 6.579 3.6c5.745 4.288 11.542 1.547 15.07-1.095" />
            <path d="M175.956 160.84c2.736 4.87 4.193 6.238 7.226 5.828a41.509 41.509 0 0 0 9.942-3.657 20.809 20.809 0 0 0 .832-11.164 5.058 5.058 0 0 0-3.968-4.161 17.975 17.975 0 0 0-7.652-.363 12.164 12.164 0 0 0-10.3 10.971 5.312 5.312 0 0 1 3.916 2.546" />
            <path d="M109.337 190.333c2.558 3.731 11.345-2.754 15.1-5.8a6.993 6.993 0 0 0-.219-2.358c-1.02-3.839-3.417-6.712-7.563-7.7a11.53 11.53 0 0 0-14.16 11.237c2.532.926 5.31 2.383 6.841 4.616" />
          </g>
          <g className="fill-[#4c220f]">
            <path d="M131.65 85.506a6.43 6.43 0 0 0 5.234-2.893 10.311 10.311 0 0 0 1.607-4.4c-2.884.844-8.182 1.9-12.706.091a9.008 9.008 0 0 0-8.215.323 8.537 8.537 0 0 0 1.325 1.8 15.93 15.93 0 0 0 12.755 5.081" />
            <path d="M168.982 60.148a17.439 17.439 0 0 0 15.076 3.995c2.7-.487 5.283-1.264 7.048-3.438a7.791 7.791 0 0 0 1.615-3.4 47.6 47.6 0 0 1-19.522-1.1 30.745 30.745 0 0 0-7.108-1.217 4.166 4.166 0 0 0 .074.8 7.651 7.651 0 0 0 2.817 4.359" />
            <path d="M126.015 35.439a9.843 9.843 0 0 0 2.642-.23c3.31-.65 5.858-2.209 7.227-5.207a11.37 11.37 0 0 0 .968-3.605c-4.014 7.364-5.573 4.792-5.573 4.792a7.483 7.483 0 0 0-6.673-1 6.105 6.105 0 0 1-5.938-.63 13.624 13.624 0 0 0-3.845-2.8c.787 5.17 5.781 8.806 11.192 8.678" />
            <path d="M71.585 60.792c5.747 0 10.711-3.07 12.616-8.152.145-.386.266-.768.375-1.148-4.768 3.259-12.93 7.426-17.989 2.182-3.225-3.342-6.144-4.612-8.426-4.978.215 6.7 5.919 12.094 13.424 12.1" />
            <path d="M30.921 100.219c3.935.5 7.73-1.278 9.268-4.661A13.214 13.214 0 0 0 41.1 92.7c-5.089 2.543-12.139 5.013-15.154 1.162-1.57-2.006-2.647-2.653-3.387-2.6.009.076.014.152.023.229.547 4.375 3.4 8.093 8.337 8.725" />
            <path d="M93.879 130.454c5.372 0 8.082-1.638 9.751-5.314a13.073 13.073 0 0 0 1.007-6.406c-2.065-.159-3.925 2.309-3.925 2.309s-3.358 9.113-13.18 1.957a20.484 20.484 0 0 0-7.251-3.622 10.668 10.668 0 0 0 .881 2.97c2.518 5.476 7.669 7.631 12.717 8.106" />
            <path d="M193.615 116.714a13.282 13.282 0 0 0 5.929 1.165 16.336 16.336 0 0 0 2.994-.215c4.16-.723 6.986-3.06 8.005-6.67-10.338 7.1-12.94 4.837-12.94 4.837-3.562-4.362-6.072-6.761-7.841-8-.056.267-.121.529-.154.809a7.7 7.7 0 0 0 4.007 8.078" />
            <path d="M49.118 167.091c4.628 1.048 8.629-.628 10.57-4.956a18.385 18.385 0 0 0 1.42-5.047c-3.1 1.575-6.078 3.423-6.078 3.423s-10.4 5.882-14.911-1.368c-1.918-3.08-3.675-4.379-5.064-4.858a10.334 10.334 0 0 0 1.261 3.678c2.713 4.826 7.138 7.845 12.8 9.128" />
            <path d="M131.461 146.371a10.456 10.456 0 0 0 4.327 2.845 21.5 21.5 0 0 0 8.163 1.511c2.536.009 5.028-.322 6.555-2.625a6.057 6.057 0 0 0 .636-1.024 10.8 10.8 0 0 0 .625-2.3c-3.528 2.642-9.326 5.382-15.071 1.095a23.108 23.108 0 0 0-6.578-3.6 5.772 5.772 0 0 0 1.343 4.1" />
            <path d="M173.273 165.751c1.573 3.437 4.574 4.921 8.36 5.406 3.656.468 6.729-.551 8.836-3.356a19.349 19.349 0 0 0 2.655-4.791 41.51 41.51 0 0 1-9.942 3.657c-3.033.411-4.49-.957-7.226-5.827a5.316 5.316 0 0 0-3.916-2.547 10.647 10.647 0 0 0-.012 1.144 14.373 14.373 0 0 0 1.245 6.314" />
            <path d="M107.166 192.911a8.592 8.592 0 0 0 7.258 1.105 18.619 18.619 0 0 0 8.086-5.261 7.1 7.1 0 0 0 1.928-4.222c-3.755 3.046-12.542 9.531-15.1 5.8-1.531-2.233-4.308-3.69-6.84-4.616 0 .118-.02.228-.02.348.054 2.19 1.481 4.978 4.689 6.846" />
          </g>
        </g>
      </svg>
    </div>
  );
};

const CookieConsentModal = ({
  title,
  content,
}: {
  title: string;
  content: string;
}) => {
  const {
    modalOpened,
    consents,
    toggleConsent,
    allowAllConsents,
    allowSelectedConsents,
  } = useCookieConsent();
  if (!modalOpened) {
    return <></>;
  }

  return (
    <div
      className={cn(
        `flex flex-col gap-y-3 rounded-2xl bg-white/90 p-6 text-black drop-shadow-xl backdrop-blur-sm md:max-w-2xl`,
      )}
    >
      <h2 className="text-lg">{title}</h2>
      <div className="text-sm">
        <p>{content}</p>
      </div>
      <div className="mt-3 flex flex-col justify-end gap-3 md:flex-row md:items-center">
        <div className="mr-auto flex gap-x-3 text-xs">
          <label className="flex items-center justify-end gap-x-1 text-sm">
            <input type="checkbox" defaultChecked={true} disabled /> Nødvendige
          </label>
          <label className="flex items-center justify-end gap-x-1 text-sm">
            <input
              type="checkbox"
              checked={consents.statistic}
              onChange={() => toggleConsent("statistic")}
            />{" "}
            Statistik
          </label>
          <label className="flex items-center justify-end gap-x-1 text-sm">
            <input
              type="checkbox"
              checked={consents.marketing}
              onChange={() => toggleConsent("marketing")}
            />{" "}
            Marketing
          </label>
        </div>
        <div className="flex gap-x-3">
          <button
            onClick={allowSelectedConsents}
            className="rounded-sm border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-black transition hover:border-slate-400 max-md:w-1/2"
          >
            {!consents.statistic && !consents.marketing
              ? "Kun nødvendige"
              : "Tillad valgte"}
          </button>
          <button
            onClick={allowAllConsents}
            className="rounded-sm border border-green-700 bg-green-700 px-3 py-2 text-sm text-white transition hover:border-green-800 hover:bg-green-700 max-md:w-1/2"
          >
            Tillad alle cookies
          </button>
        </div>
      </div>
    </div>
  );
};

export default function CookieConsent({
  title,
  content,
}: {
  title: string;
  content: string;
}) {
  return (
    <CookieConsentContextProvider>
      <div className={`fixed bottom-0 right-0 z-1000 p-6 transition`}>
        <CookieConsentSymbol />
        <CookieConsentModal title={title} content={content} />
      </div>
    </CookieConsentContextProvider>
  );
}
